import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Functions from '../../api/Functions';
import { signUpStatus } from '../../constants/constants';
import { getCountryAndProvince } from '../../utils/CountryUtil';
import { firestoreConnect } from 'react-redux-firebase';
import { compose } from 'redux';
import { daysFromDateToToday, dateToMMMMDoYYYY } from '../../utils/DateUtil';
import M from 'materialize-css';

const ProfileSummary = ({ profile, showInviteCodeModal, sent, firestore, showDeleteConfirmation, updateProfiles }) => {
  const [useCache, setUseCache] = useState(profile.useCache ? profile.useCache : false);
  const [offline, setOffline] = useState(profile.offline ? profile.offline : false);
  const [countryName, provinceName] = getCountryAndProvince(profile.countryCode, profile.stateCode);
  const [wasInviteCodeSent, setWasInviteCodeSent] = useState(false);
  const [inviteCodeSent, setInviteCodeSent] = useState('');
  const [daysSinceLastInviteCode, setDaysSinceLastInviteCode] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const finishSignUpFailed = profile.signUpStatus ? profile.signUpStatus === signUpStatus.FINISH_SIGN_UP_FAILED : false;
  const isOnWaitingList = profile.signUpStatus ? profile.signUpStatus === signUpStatus.ON_WAITING_LIST : false;

  useEffect(() => M.AutoInit(), []);

  const getSentInviteCodes = useCallback(async (profile, firestore) => {
    try {
      const querySnapshot = await firestore.collection('inviteCodes').where('sentTo', '==', profile.email).get();

      setWasInviteCodeSent(!querySnapshot.empty);

      let tempDoc;

      querySnapshot.forEach((doc) => {
        tempDoc = { id: doc.id, ...doc.data() };
      });

      setInviteCodeSent(tempDoc.code);

      if (tempDoc.dateSent) {
        setDaysSinceLastInviteCode(getDaysSinceLastInviteCode(tempDoc.dateSent));
      }
    } catch (error) {
      console.warn('Get sent inviteCodes', error);
    }
  }, []);

  useEffect(() => {
    if (profile && isOnWaitingList) {
      getSentInviteCodes(profile, firestore);
    }
  }, [profile, isOnWaitingList, firestore, getSentInviteCodes]);

  const getDaysSinceLastInviteCode = (date) => {
    return daysFromDateToToday(date.toDate());
  };

  const getDaysInWaitingList = () => {
    return daysFromDateToToday(profile.createdAt.toDate());
  };

  const updateUseCache = () => {
    firestore
      .collection('profiles')
      .doc(profile.id)
      .update({
        useCache: !useCache,
      })
      .then(() => {
        setUseCache(!useCache);
      });
  };

  const updateOffline = () => {
    firestore
      .collection('profiles')
      .doc(profile.id)
      .set({ offline: !offline }, { merge: true })
      .then(() => setOffline(!offline));
  };

  const retryToFinishSignUp = () => {
    setIsLoading(true);
    Functions.retryToFinishSignUp(profile.id)
      .then(({ data }) => {
        const { success, message } = data;
        M.toast({ html: success ? 'Finished Sign Up' : message });
        clearProfileSearchOnCondition(success);
      })
      .catch((e) => {
        M.toast({ html: e && e.message ? e.message : 'Something went wrong' });
      })
      .finally(() => setIsLoading(false));
  };

  const clearProfileSearchOnCondition = (clear) => {
    if (clear) updateProfiles();
  };

  const renderProfileInfo = () => (
    <div>
      <p>Sign up Status: {profile.signUpStatus}</p>
      <p>Platform: {profile.platform && profile.platform}</p>
      <p>Version: {profile.currentVersion}</p>
      <p>Country: {countryName && countryName}</p>
      <p>State/Province: {provinceName && provinceName}</p>
      <p>Sign up date: {profile.createdAt && dateToMMMMDoYYYY(profile.createdAt.toDate())}</p>
      {isOnWaitingList && profile.createdAt ? <p>Days on Waitlist: {getDaysInWaitingList()}</p> : ''}
      {isOnWaitingList && wasInviteCodeSent && (
        <>
          <p>Days Since Last Invite Code: {daysSinceLastInviteCode}</p>
          <p>Last Invite Code: {inviteCodeSent}</p>
        </>
      )}
      <div className="row">
        {renderSwitch('Use Cache', useCache, updateUseCache)}
        {renderSwitch('Offline (pyns cache)', offline, updateOffline)}
      </div>
    </div>
  );

  const renderSwitch = (label, field, onChange) => (
    <div className="switch col s6">
      <label>{label}</label>
      <label>
        <input type="checkbox" checked={field} onChange={onChange} />
        <span className="lever" />
      </label>
    </div>
  );

  const renderInviteCodeOption = () =>
    isOnWaitingList && (
      <Link to={window.location} onClick={() => showInviteCodeModal(profile)}>
        SEND INVITE CODE
      </Link>
    );

  const renderRetrySignUpOption = () =>
    finishSignUpFailed && (
      <Link to={window.location} onClick={() => retryToFinishSignUp()}>
        Retry sign up
      </Link>
    );

  const renderLoading = () => (
    <div className="progress">
      <div className="indeterminate"></div>
    </div>
  );

  return (
    <div className="col s12 m6">
      <div className="card z-depth-0 project-summary">
        <div key={profile.id}>
          <div className="card-content grey-text text-darken-3">
            <span className="card-email">
              <b>Email: {profile.email}</b>
              {(wasInviteCodeSent || sent) && isOnWaitingList && <span className="new badge" data-badge-caption="Invitation code sent"></span>}
              <br />
              <b>Username: {profile.userName}</b>
              <br />
              <b>
                Name: {profile.name} {profile.lastName}
              </b>
              <br />
              <b>ID: {profile.id}</b>
              <br />
            </span>
            <br />
            {renderProfileInfo()}
            {isLoading && renderLoading()}
          </div>
        </div>
        <div className="card-action">
          <a href="#delete" onClick={() => showDeleteConfirmation(profile)}>
            Delete
          </a>
          <a href="#sendNotification" onClick={() => Functions.sendDailyNotification(profile.id)}>
            SEND NOTIFICATION
          </a>
          <Link to={'/profile/' + profile.id}>More</Link>
          {renderInviteCodeOption()}
          {renderRetrySignUpOption()}
        </div>
      </div>
    </div>
  );
};

export default compose(firestoreConnect())(ProfileSummary);
